/** @file Descriptions for Configuration routes */
import React from 'react';
import { Link, Stack } from '@mui/material';
import { MerchantAwareLink as RouterLink } from '../../../components/core/MerchantAwareLink';
import { configurationPaths } from '../../../util/constants';
import { useMerchant, isMerchantIdParentPickerRow } from '../../../contexts/MerchantContext';
import PageWrapper from '../../../common/PageWrapper';
import { useGetMerchantQuery } from '../../../store/modules/merchant/api';
import { ChildMerchantsLandingView } from './components/ChildMerchantsLandingView';
import { ParentMerchantsSelectionView } from './components/ParentMerchantsSelectionView';
import { InfoCopy } from './components/InfoCopy';
import { SubTitleDisplay } from './components/SubTitleDisplay';

export const Configuration = () => {
  const {
    merchantId,
    isChildMerchantOnlyUser,
    partnerMerchantId,
    requiresParentSelection,
    pendingChildMerchantLandingAfterParentPick,
    parentMerchants,
  } = useMerchant();

  const merchantIdIsParentPickerRow = isMerchantIdParentPickerRow(merchantId, parentMerchants);

  /** Skip private merchant fetch while partner is still resolving (not on child landing / picker row). */
  const partnerContextLoading =
    isChildMerchantOnlyUser &&
    partnerMerchantId === undefined &&
    !requiresParentSelection &&
    !pendingChildMerchantLandingAfterParentPick &&
    !merchantIdIsParentPickerRow;
  const showChildMerchantsLanding =
    isChildMerchantOnlyUser &&
    !requiresParentSelection &&
    (partnerMerchantId === null ||
      pendingChildMerchantLandingAfterParentPick ||
      merchantIdIsParentPickerRow);

  const { data: merchantData, isLoading: isLoadingApiKey } = useGetMerchantQuery(merchantId, {
    skip:
      !merchantId || showChildMerchantsLanding || partnerContextLoading || requiresParentSelection,
  });

  return (
    <PageWrapper
      title="Welcome to the Skipify Merchant Portal!"
      description="This portal will allow you to set various parameters for your particular Skipify integration. Use the menu on the left to navigate to a specific section of the portal for configuration."
      isLoading={
        partnerContextLoading ||
        (!showChildMerchantsLanding && !requiresParentSelection && isLoadingApiKey)
      }
    >
      {requiresParentSelection && isChildMerchantOnlyUser ? (
        <ParentMerchantsSelectionView />
      ) : showChildMerchantsLanding ? (
        <ChildMerchantsLandingView />
      ) : (
        <>
          <Stack spacing={2} sx={{ mb: 5 }}>
            <InfoCopy title="Merchant ID" infoString={merchantId} />
            <InfoCopy
              title="Legacy HMAC API key"
              infoString={merchantData?.apiKey ?? ''}
              additionalContent={
                <Link
                  component={RouterLink}
                  to={configurationPaths.API_KEYS}
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    textDecoration: 'none',
                  }}
                >
                  View API Keys
                </Link>
              }
            />
          </Stack>
          <Stack spacing={4}>
            <SubTitleDisplay
              subTitle="Payments & Processing"
              message="Use this page to configure your Payment Capture and Payment Gateway options."
              route={configurationPaths.PAYMENTS_AND_PROCESSING}
            />
            <SubTitleDisplay
              subTitle="Merchant domains"
              message="You can add one or more URLs here that will be whitelisted so that we can communicate with your applications."
              route={configurationPaths.MERCHANT_DOMAINS}
            />
            <SubTitleDisplay
              subTitle="Branding"
              message="Use this page to add your branding to Skipify features, such as Pay By Link and Purchase Details."
              route={configurationPaths.BRANDING}
            />
            <SubTitleDisplay
              subTitle="Fulfillment"
              message="Fulfillment will allow you to select options for items such as shipping and tax, as well as specifying the URL where we perform callbacks."
              route={configurationPaths.FULFILLMENT}
            />
            <SubTitleDisplay
              subTitle="Webhooks"
              message="Skipify provides various webhooks to which you can subscribe."
              route={configurationPaths.MERCHANT_WEBHOOKS}
            />
            <SubTitleDisplay
              subTitle="Paylinks"
              message="Use this page to configure your Paylinks."
              route={configurationPaths.PAYLINKS}
            />
            <SubTitleDisplay
              subTitle="Tipping"
              message="Use this page to toggle whether or not tipping will be shown during checkout."
              route={configurationPaths.TIPPING}
            />
          </Stack>
        </>
      )}
    </PageWrapper>
  );
};
